import { Link } from 'react-router-dom'

import { Page, PageHeader } from '@/components/ui/Page'
import { ErrorState, Loading } from '@/components/ui/States'
import { cn } from '@/lib/cn'
import { daysUntil, formatDateShort, today } from '@/lib/dates'
import { jobName } from '@/lib/jobs'
import { pluralise } from '@/lib/format'
import { deliverableDue, isDelivered } from '@/lib/deliverables'
import { describeError } from '@/lib/supabase'
import { useEditingQueue, useSetDelivered, type EditingJob } from '@/features/deliverables/queries'

type Queue = 'photo' | 'film'

interface Row {
  job: EditingJob
  items: Array<{ item: EditingJob['deliverables'][number]; due: string | null; delivered: boolean }>
  late: boolean
}

/** One queue: weddings that have happened with something of this kind still to deliver. */
function queueRows(jobs: EditingJob[], kind: Queue, onDate: string): Row[] {
  const rows: Row[] = []
  for (const job of jobs) {
    if (kind === 'photo' ? job.photos_delivered_at : job.film_delivered_at) continue
    const items = job.deliverables
      .filter((item) => !item.internal && item.kind === kind)
      .sort((a, b) => a.sort_order - b.sort_order)
      .map((item) => ({ item, due: deliverableDue(item, job), delivered: isDelivered(item) }))
    const open = items.filter((entry) => !entry.delivered)
    if (open.length === 0) continue
    rows.push({ job, items, late: open.some((entry) => entry.due !== null && entry.due < onDate) })
  }
  // In the order the weddings happened, oldest first, so ticking something
  // never moves a wedding (27 Oct 2026).
  return rows.sort((a, b) => a.job.event_date.localeCompare(b.job.event_date))
}

/**
 * The editing queues, Photo and Film: each wedding that has happened, with a
 * tick box per deliverable from its package and the date it is due (Jena's
 * turnaround: sneak peeks 3 days, gallery 8 weeks, trailer 2 weeks, highlight
 * film 12 weeks; lib/deliverables.ts). Ticking marks it delivered to the
 * couple; a wedding leaves the queue when everything in it is ticked. Oldest
 * wedding first.
 */
export function EditingPage() {
  const onDate = today()
  const query = useEditingQueue(onDate)

  if (query.isPending) return <Loading label="Loading the editing queues" />
  if (query.isError) {
    return (
      <Page>
        <ErrorState title="The queues could not load" detail={describeError(query.error)} onRetry={() => void query.refetch()} />
      </Page>
    )
  }

  const jobs = query.data ?? []
  const photo = queueRows(jobs, 'photo', onDate)
  const film = queueRows(jobs, 'film', onDate)
  const late = [...photo, ...film].filter((row) => row.late).length

  return (
    <Page>
      <PageHeader
        eyebrow="Operations"
        title="Editing"
        meta={
          <>
            <span>{pluralise(photo.length, 'wedding')} in photo</span>
            <span>{pluralise(film.length, 'wedding')} in film</span>
            {late > 0 ? <span className="text-clay">{late} past a due date</span> : null}
          </>
        }
      />

      <div className="grid gap-8 lg:grid-cols-2">
        <QueueList title="Photo" rows={photo} onDate={onDate} />
        <QueueList title="Film" rows={film} onDate={onDate} />
      </div>
    </Page>
  )
}

function QueueList({ title, rows, onDate }: { title: string; rows: Row[]; onDate: string }) {
  const setDelivered = useSetDelivered()

  return (
    <section className="border-t border-t-[var(--border-hairline)] pt-4">
      <h2 className="mb-3 text-[length:var(--text-base)]">
        {title} <span className="text-taupe">({rows.length})</span>
      </h2>

      {rows.length === 0 ? (
        <p className="text-[length:var(--text-sm)] text-taupe">Nothing waiting. Everything promised has gone out.</p>
      ) : (
        <ul className="flex flex-col">
          {rows.map(({ job, items }) => (
            <li key={job.id} className="border-b py-2.5">
              <Link to={`/weddings/${job.id}`} className="display text-[0.9375rem] hover:underline">
                {jobName(job)}
              </Link>
              <ul className="mt-1.5 flex flex-col gap-1.5">
                {items.map(({ item, due, delivered }) => {
                  const days = due ? daysUntil(due, onDate) : null
                  return (
                    <li key={item.id} className="flex items-center justify-between gap-3">
                      <label className="flex min-w-0 items-center gap-2.5 text-[length:var(--text-sm)]">
                        <input
                          type="checkbox"
                          checked={delivered}
                          disabled={setDelivered.isPending}
                          onChange={(event) => setDelivered.mutate({ jobId: job.id, item, delivered: event.target.checked })}
                          className="check size-[15px] shrink-0 appearance-none rounded-none border border-ink bg-transparent transition-colors duration-[var(--dur-fast)] checked:bg-ink checked:bg-center checked:bg-no-repeat disabled:opacity-40"
                        />
                        <span className={cn('truncate', delivered && 'text-taupe line-through')}>{item.title}</span>
                      </label>
                      {due ? (
                        <span
                          className={cn(
                            'shrink-0 text-[length:var(--text-2xs)]',
                            delivered ? 'text-taupe' : days !== null && days < 0 ? 'text-clay' : days !== null && days <= 7 ? 'text-amber' : 'text-taupe',
                          )}
                          data-numeric
                        >
                          {formatDateShort(due)}
                        </span>
                      ) : null}
                    </li>
                  )
                })}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  )
}
